
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import curved9 from "assets/images/preview.png";
import toast from "react-hot-toast";
import { baseURL } from "utils/api";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import BasicLayout from "../components/layout";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import CircularProgress from '@mui/material/CircularProgress';

function VerifyEmail() {

    const navigate = useNavigate();
    const location = useLocation();

    const [token, setToken] = useState("");
    const [status, setStatus] = useState("loading"); // loading | success | error
    const [errorMessage, setErrorMessage] = useState("");
    const [countdown, setCountdown] = useState(5);
    const params = new URLSearchParams(window.location.search);
    const isCustomerValue = params.get("isCustomer") === "true";
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenFromUrl = queryParams.get("token");
        setStatus("loading");
        setErrorMessage("");

        if (!tokenFromUrl || tokenFromUrl.trim().length === 0) {
            setStatus("error");
            const msg = "Invalid verification token";
            setErrorMessage(msg);
            toast.error(msg);
            return;
        }

        setToken(tokenFromUrl);
        fetch(`${baseURL}verifyEmail?token=${encodeURIComponent(tokenFromUrl)}`)
            .then((res) => res.json())
            .then((data) => {
                if (data?.success) {
                    toast.success(data?.message || "Email verified successfully");
                    setStatus("success");

                    if (!isCustomerValue) {
                        setTimeout(() => {
                            navigate("/login");
                        }, 5000);
                    }
                } else {
                    const msg = data?.message || "Invalid or expired verification token";
                    setStatus("error");
                    setErrorMessage(msg);
                    toast.error(msg);
                }
            })
            .catch((err) => {
                const msg = err?.message || "Something went wrong";
                setStatus("error");
                setErrorMessage(msg);
                toast.error(msg);
            });
    }, [location]);

    // countdown timer + redirect
    useEffect(() => {
        if (status === "success") {
            const timer = setInterval(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [status, countdown, navigate]);


    return (
        <BasicLayout>
            <SoftBox
                sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh" }}
            >
                {
                    status === "success" ?
                        <SoftBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            p={4}
                            borderRadius="xl"
                            boxShadow="lg"
                            bgColor="white"
                            textAlign="center"
                        >
                            <MarkEmailReadIcon fontSize="large" sx={{ color: "#4caf50", mb: 2 }} />

                            <SoftTypography variant="h4" fontWeight="bold" mb={2}>
                                Email Verified Successfully
                            </SoftTypography>

                            {isCustomerValue ? <SoftTypography variant="body2" color="text" mb={3} sx={{ maxWidth: "600px", mx: "auto" }}>
                                Thank you for verifying your email address. Your account is now active and ready to use.
                            </SoftTypography> :
                                <SoftTypography variant="body2" color="text" mb={3} sx={{ maxWidth: "600px", mx: "auto" }}>
                                    Thank you for verifying your email address. Your account is now active and ready to use. You will be redirected to the login page in <strong>{countdown}</strong> second{countdown !== 1 && "s"}... If you are not redirected automatically, please click the button below to continue.
                                </SoftTypography>}

                            {!isCustomerValue && <Link to="/authentication/sign-in">
                                <SoftButton variant="gradient" color="info">
                                    Go to Login
                                </SoftButton>
                            </Link>}
                        </SoftBox>

                        :
                        status === "error" ?
                            <SoftBox
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                p={4}
                                borderRadius="xl"
                                boxShadow="lg"
                                bgColor="white"
                                textAlign="center"
                            >
                                <SoftTypography variant="h4" fontWeight="bold" mb={2}>
                                    Email Verification Failed
                                </SoftTypography>

                                <SoftTypography variant="body2" color="text" mb={3} sx={{ maxWidth: 650, mx: "auto" }}>
                                    {errorMessage || "Invalid or expired verification token"}
                                </SoftTypography>

                                <Link to="/login">
                                    <SoftButton variant="gradient" color="info">
                                        Go to Login
                                    </SoftButton>
                                </Link>
                            </SoftBox>
                            :
                        <SoftBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            p={4}
                            borderRadius="xl"
                            boxShadow="lg"
                            bgColor="white"
                            textAlign="center"
                        >
                            <CircularProgress size={40} sx={{ color: "#4caf50", mb: 3 }} />

                            <SoftTypography variant="h4" fontWeight="bold" mb={2}>
                                Email Verification
                            </SoftTypography>

                            <SoftTypography variant="body2" color="text" sx={{ maxWidth: 650, mx: "auto" }}>
                                Verifying your email address... This will only take a moment. You’ll be redirected shortly.
                            </SoftTypography>
                        </SoftBox>

                }
            </SoftBox>
        </BasicLayout>
    );
}

export default VerifyEmail;
